<script lang="ts">
import ExpandableContainer from '@/V2/components/expandable-container/ExpandableContainer.vue';
import ExpandableContainerItem from '@/V2/components/expandable-container/ExpandableContainerItem.vue';
import CustomerEmptyState from '@/V2/customers/CustomerEmptyState.vue';
import { mapGetters } from 'vuex';
import { RUN_STATUS } from '@/constants/analyst-constants';
import { RISK_LEVEL, riskLevelToReadableLabel } from '@/constants/riskLevels';
import CustomerListLoadingBars from '@/V2/customers/CustomerListLoadingBars.vue';
import CustomerFraudSignalsDetails from '@/V2/customers/fraud/CustomerFraudSignalsDetails.vue';
import {
  formatSignal,
  formatCompliance,
  getSignalTitle,
  getSignalSubtitle,
  customerHasAnyHighRiskDocuments,
} from './insight.utils';
import {
  iButton,
  iButtonTypes,
  iButtonStyles,
  iButtonSizes,
  iKeyIcon,
  iKeyIconType,
  iKeyIconStyle,
} from '@inscribe/signal';
import StatusBadge from '@/V2/components/status/StatusBadge.vue';
import CustomerComplianceCheckDetails from '@/V2/customers/business-compliance-agent/CustomerComplianceCheckDetails.vue';
import { VERIFICATION_RESULT } from '@/constants/analyst-constants';

interface InsightCategory {
  label: string;
  values: string[];
}

const UI_INSIGHT_CATEGORIES: InsightCategory[] = [
  {
    label: riskLevelToReadableLabel(RISK_LEVEL.HIGH),
    values: [RISK_LEVEL.HIGH, VERIFICATION_RESULT.HIGH_RISK],
  },
  {
    label: riskLevelToReadableLabel(RISK_LEVEL.MEDIUM),
    values: [RISK_LEVEL.MEDIUM, VERIFICATION_RESULT.INCONCLUSIVE],
  },
  {
    label: riskLevelToReadableLabel(RISK_LEVEL.LOW),
    values: [RISK_LEVEL.LOW, VERIFICATION_RESULT.LOW_RISK],
  },
  {
    label: riskLevelToReadableLabel(RISK_LEVEL.NOT_APPLICABLE),
    values: [RISK_LEVEL.NOT_APPLICABLE],
  },
];

export default {
  components: {
    ExpandableContainer,
    ExpandableContainerItem,
    CustomerListLoadingBars,
    CustomerFraudSignalsDetails,
    CustomerEmptyState,
    StatusBadge,
    CustomerComplianceCheckDetails,
    iButton,
    iKeyIcon,
  },
  data() {
    return {
      selectedSignal: null,
      iButtonTypes,
      iButtonStyles,
      iButtonSizes,
      iKeyIconType,
      iKeyIconStyle,
      getSignalTitle,
      getSignalSubtitle,
      RISK_LEVEL,
      riskLevelToReadableLabel,
    };
  },
  computed: {
    ...mapGetters([
      'getCachedCustomerSignalsById',
      'getCachedAggregatedDocumentResults',
    ]),
    showEmptyState() {
      return !this.allCustomerInsights?.length;
    },
    rawInsights() {
      return (
        this.getCachedCustomerSignalsById(this.$route.params.customer_id)
          ?.data || {}
      );
    },
    analysisHasRun() {
      return typeof this.rawInsights !== 'undefined';
    },
    agentIsRunning() {
      return (
        this.getCachedCustomerSignalsById(this.$route.params.customer_id)
          ?.status === RUN_STATUS.RUNNING
      );
    },
    allCustomerInsights() {
      const customerInsights = [];
      for (let signalKey of Object.keys(this.rawInsights || {})) {
        const formattedSignal = formatSignal(
          signalKey,
          this.rawInsights[signalKey],
          customerHasAnyHighRiskDocuments(this.aggregatedDocumentsResults),
        );
        if (formattedSignal) {
          if (Object.keys(formattedSignal).length > 0) {
            customerInsights.push(formattedSignal);
          }
        } else {
          customerInsights.push(formatCompliance(this.rawInsights[signalKey]));
        }
      }
      return customerInsights;
    },
    aggregatedDocumentsResults() {
      return this.getCachedAggregatedDocumentResults(
        this.$route.params.customer_id,
      );
    },
    insightsByRiskLevel() {
      const insightsByRiskLevel: Record<string, CustomerInsight[]> = {};
      UI_INSIGHT_CATEGORIES.map((riskLevel) => {
        insightsByRiskLevel[riskLevel.label] = this.allCustomerInsights.filter(
          (insight) => riskLevel.values.includes(insight.result),
        );
      });
      return insightsByRiskLevel;
    },
  },
  methods: {
    onSignalClick(signal) {
      this.selectedSignal = signal;
    },
    hideDrawer() {
      this.selectedSignal = null;
    },
    onViewAllInsightsButtonClick() {
      this.$router.push({
        name: 'customer-insights',
        params: { customer_id: this.$route.params.customer_id },
      });
    },
  },
};
</script>
<template>
  <div class="customer-insights">
    <ExpandableContainer
      title="Insights"
      :subtitle="agentIsRunning ? 'Analyzing...' : null"
    >
      <template #tools>
        <i-button
          v-if="allCustomerInsights?.length > 0 || !agentIsRunning"
          label="View by Category"
          :size="iButtonSizes.SMALL"
          :type="iButtonTypes.NEUTRAL"
          :button-style="iButtonStyles.OUTLINE"
          @button-click="onViewAllInsightsButtonClick"
        />
      </template>

      <template #items>
        <div class="slot-content">
          <!-- loading state -->
          <customer-list-loading-bars v-if="agentIsRunning">
          </customer-list-loading-bars>

          <!-- empty state -->
          <customer-empty-state
            v-else-if="showEmptyState"
            title="No Customer Insights"
            :description="
              analysisHasRun
                ? 'Analysis complete. No additional insights at the customer level'
                : 'Customer-level signals will appear here'
            "
            icon="medium_user_single"
            :show-button="false"
            data-testid="customer-fraud-signals-empty-state"
          />

          <!-- loaded state -->
          <div v-else class="list-groups-container d-flex flex-column i-gap-2">
            <template
              v-for="riskLevel in Object.keys(insightsByRiskLevel)"
              :key="riskLevel"
            >
              <div
                v-if="insightsByRiskLevel[riskLevel].length > 0"
                class="list-group d-flex flex-column i-radius-3xl"
              >
                <div class="title i-text-subheading-3 i-px-9 i-py-5">
                  {{ riskLevel }}
                </div>
                <div class="insights-list items-list d-flex flex-column">
                  <expandable-container-item
                    v-for="insight in insightsByRiskLevel[riskLevel]"
                    :key="getSignalTitle(insight)"
                    :title="getSignalTitle(insight)"
                    :subtitle="getSignalSubtitle(insight)"
                    @on-item-click="onSignalClick(insight)"
                    @on-right-icon-click="onSignalClick(insight)"
                    class="insight-item item-card"
                    data-testid="customer-fraud-signal-item"
                  >
                    <template #left-container>
                      <i-key-icon
                        v-if="insight.icon"
                        :icon="insight.icon"
                        :type="insight.iconType"
                        :icon-style="iKeyIconStyle.SOLID"
                        :use-solid-icon-colour="true"
                      ></i-key-icon>
                    </template>

                    <template #right-container>
                      <status-badge
                        :state="insight.status"
                        :label="insight.badgeLabel"
                      ></status-badge>
                    </template>
                  </expandable-container-item>
                </div>
              </div>
            </template>
          </div>
        </div>
      </template>
    </ExpandableContainer>

    <!-- details view -->
    <customer-fraud-signals-details
      v-if="selectedSignal?._type === 'signal'"
      :iDrawerVisible="!!selectedSignal"
      :selected-signal="selectedSignal"
      :aggregated-documents-results="aggregatedDocumentsResults?.fraud_results"
      @hide-drawer="hideDrawer"
    />

    <!-- details drawer -->
    <customer-compliance-check-details
      v-if="selectedSignal?._type === 'business_check'"
      :open="!!selectedSignal"
      :check="selectedSignal"
      @close="hideDrawer"
    />
  </div>
</template>

<style lang="scss">
.customer-insights {
  .signals-container {
    gap: 1px;
  }
}
</style>
